import { Download, RotateCw } from 'lucide-react';
import { useState } from 'react';
import { EInvoiceChip } from '@/components/einvoicing/einvoice-chip';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ApiError } from '@/lib/api';
import { useMe } from '@/lib/clients';
import {
  canResubmit,
  describeEvent,
  downloadXml,
  formatDateTime,
  isLocked,
  useEInvoice,
  useResubmit,
  type EInvoiceDetail,
} from '@/lib/einvoicing';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

/**
 * E-invoice strip on an approved invoice or credit note (document 06 "E-invoice on invoices"):
 * status, ASP reference, what went wrong, and the actions Timeline, Download XML and Resubmit.
 * Renders nothing while e-invoicing is off or the document has no e-invoice.
 */
export function EInvoicePanel({ documentId, label }: { documentId: string; label: string }) {
  const q = useEInvoice(documentId);
  const [open, setOpen] = useState(false);
  const s = q.data?.submission;
  if (!q.data?.enabled || !s) return null;
  const latest = q.data.payloads[0];
  return (
    <div
      data-testid="einvoice-panel"
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-md border px-4 py-2.5 text-sm',
        s.status === 'rejected' || s.needsAttention
          ? 'border-destructive/30 bg-destructive/5'
          : 'bg-card',
      )}
    >
      <span className="font-medium">E-invoice</span>
      <EInvoiceChip status={s.status} needsAttention={s.needsAttention} />
      <span className="min-w-48 flex-1 text-muted-foreground">
        {summary(q.data)}
        {isLocked(s)
          ? ` This ${label.toLowerCase()} can no longer be edited or voided; correct it with a credit note.`
          : ''}
      </span>
      <Button variant="link" size="sm" className="px-1" onClick={() => setOpen(true)}>
        Timeline
      </Button>
      {latest ? <XmlButton payload={latest} number={s.number} /> : null}
      <ResubmitButton detail={q.data} documentId={documentId} />
      <TimelineSheet
        open={open}
        onOpenChange={setOpen}
        detail={q.data}
        label={label}
        documentId={documentId}
      />
    </div>
  );
}

function summary(d: EInvoiceDetail): string {
  const s = d.submission!;
  switch (s.status) {
    case 'not_required':
      return s.statusReason ?? 'No e-invoice is required for this document.';
    case 'accepted':
      return `Accepted by the service provider, reference ${s.aspReference}.`;
    case 'submitted':
      return `Submitted, reference ${s.aspReference}; waiting for the outcome.`;
    case 'rejected':
      return `${s.errors.map((e) => e.message).join('; ') || 'See the timeline for the reason'}.`;
    default:
      return s.needsAttention
        ? `Delivery failed ${s.attempts} times (${s.lastError ?? 'service provider unavailable'}). Retry when the provider is back.`
        : s.attempts > 0
          ? `Delivery failed; trying again${s.nextAttemptAt ? ` at ${formatDateTime(s.nextAttemptAt)}` : ''}.`
          : 'Queued; it is built, checked and sent within a minute.';
  }
}

function XmlButton({
  payload,
  number,
}: {
  payload: { id: string; version: number };
  number: string;
}) {
  const orgId = useOrgId();
  return (
    <Button
      variant="link"
      size="sm"
      className="gap-1 px-1"
      onClick={() =>
        void downloadXml(orgId!, payload, `${number}-v${payload.version}.xml`).catch((e: unknown) =>
          toast(e instanceof Error ? e.message : String(e), { tone: 'error' }),
        )
      }
    >
      <Download className="size-3.5" /> Download XML
    </Button>
  );
}

function ResubmitButton({ detail, documentId }: { detail: EInvoiceDetail; documentId: string }) {
  const me = useMe();
  const resubmit = useResubmit();
  const s = detail.submission!;
  if (!me.data?.isStaff || !canResubmit(s)) return null;
  return (
    <Button
      size="sm"
      variant="outline"
      className="gap-1"
      disabled={resubmit.isPending}
      onClick={() =>
        resubmit.mutate(documentId, {
          onSuccess: () =>
            toast(
              s.status === 'rejected'
                ? `${s.number} resubmitted. A new version is built from the current details.`
                : `${s.number} queued for delivery again.`,
            ),
          onError: (e) => toast(e instanceof ApiError ? e.message : String(e), { tone: 'error' }),
        })
      }
    >
      <RotateCw className="size-3.5" /> {s.status === 'rejected' ? 'Resubmit' : 'Retry delivery'}
    </Button>
  );
}

/** Drawer with every event, the errors of each version and the XML of every version. */
function TimelineSheet({
  open,
  onOpenChange,
  detail,
  label,
  documentId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  detail: EInvoiceDetail;
  label: string;
  documentId: string;
}) {
  const s = detail.submission!;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-[520px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            E-invoice for {label.toLowerCase()} {s.number}
            <EInvoiceChip status={s.status} needsAttention={s.needsAttention} />
          </SheetTitle>
          <SheetDescription>
            {s.aspReference ? `Service provider reference ${s.aspReference}. ` : ''}
            Every step is kept, with each version that was built.
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-6 px-4 pb-6">
          {s.errors.length ? (
            <section className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <h3 className="mb-1 font-medium">
                {s.errors[0]!.source === 'validation'
                  ? 'Fix these and resubmit'
                  : 'The service provider said'}
              </h3>
              <ul className="list-disc space-y-1 pl-4">
                {s.errors.map((e, i) => (
                  <li key={i}>
                    {e.message}
                    {e.rule || e.code ? (
                      <span className="text-muted-foreground">
                        {' '}
                        ({e.rule ?? e.code}
                        {e.term ? `, ${e.term}` : ''})
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
              <div className="mt-3">
                <ResubmitButton detail={detail} documentId={documentId} />
              </div>
            </section>
          ) : null}
          <section>
            <h3 className="mb-3 text-sm font-semibold">Timeline</h3>
            <ol
              className="relative flex flex-col gap-4 border-l pl-5"
              data-testid="einvoice-timeline"
            >
              {[...detail.events].reverse().map((e) => {
                const d = describeEvent(e);
                return (
                  <li key={e.id} className="relative text-sm">
                    <span
                      aria-hidden
                      className={cn(
                        'absolute top-1.5 -left-[25px] size-2.5 rounded-full border-2 border-card',
                        e.event === 'accepted'
                          ? 'bg-success'
                          : e.event === 'rejected' ||
                              e.event === 'validation_failed' ||
                              e.event === 'delivery_failed'
                            ? 'bg-destructive'
                            : 'bg-primary',
                      )}
                    />
                    <div className="font-medium">{d.title}</div>
                    <div className="text-xs text-muted-foreground tnum">{formatDateTime(e.at)}</div>
                    {d.text ? <p className="mt-0.5 text-muted-foreground">{d.text}</p> : null}
                  </li>
                );
              })}
            </ol>
          </section>
          {detail.payloads.length ? (
            <section>
              <h3 className="mb-2 text-sm font-semibold">Versions</h3>
              <ul className="divide-y rounded-md border text-sm">
                {detail.payloads.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 px-3 py-2">
                    <div className="flex-1">
                      <div className="font-medium">Version {p.version}</div>
                      <div className="text-xs text-muted-foreground">
                        Built {formatDateTime(p.builtAt)} ·{' '}
                        {p.valid
                          ? 'passed validation'
                          : `${p.findings.filter((f) => f.severity === 'error').length} validation errors`}
                      </div>
                    </div>
                    <XmlButton payload={p} number={s.number} />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
